import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Form, Formik } from 'formik';
import { MultiSelectFormField, SelectOption } from 'lib/form/MultiSelectFormField';
import { PickUpIcon } from 'lib/icons/PickUpIcon';
import { MerchantBrandedDialog } from 'lib/MerchantBrandedDialog';
import { LoadingButton } from 'ui/LoadingButton';
import { getDay, getTimeslotTimes } from '../helpers';
import { Timeslot } from '../model/Timeslot';

const PREFIX = 'TimeslotPickerDialog';

const classes = {
    selectBox: `${PREFIX}selectBox`,
    selectBoxList: `${PREFIX}selectBoxList`,
    boldText: `${PREFIX}boldText`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.selectBox}`]: {
        maxHeight: '200px',
        borderRadius: theme.shape.borderRadius
    },

    [`& .${classes.selectBoxList}`]: {
        padding: theme.spacing(0)
    },

    [`& .${classes.boldText}`]: {
        fontWeight: theme.typography.fontWeightBold
    }
}));

const StyledPickUpIcon = styled(PickUpIcon)(({ theme }) => ({
    stroke: theme.palette.background.default,
    fontSize: 'inherit'
}));

enum OrderAheadTypes {
    ASAP = 'ASAP',
    Later = 'Later'
}

type TimeslotFormData = {
    day: string;
    time: Record<string, string>;
};

interface TimeslotPickerDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data?: { day: string; time: string }) => void;
    onCancelOrder?: () => void;
    options: Record<string, Timeslot[]>;
    timeslot?: Timeslot;
    noAsap?: boolean;
    isLoading?: boolean;
    noTimeslots?: boolean;
    description?: string;
    confirmButton?: string;
    cancelButton?: string;
    branded?: boolean;
}

export const TimeslotPickerDialog: React.FC<React.PropsWithChildren<TimeslotPickerDialogProps>> = ({
    open,
    onClose,
    onSubmit,
    timeslot,
    options,
    noAsap,
    noTimeslots,
    description,
    confirmButton,
    cancelButton,
    onCancelOrder,
    branded = true
}) => {
    const { t } = useTranslation();
    const [orderAheadType, setOrderAheadType] = React.useState<string>(
        timeslot ? OrderAheadTypes.Later : OrderAheadTypes.ASAP
    );
    const dayOptions = React.useMemo(
        () => Object.keys(options).map(item => ({ value: item, label: item })),
        [options]
    );
    const timeOptions = React.useMemo(
        () =>
            Object.entries(options).reduce<Record<string, SelectOption[]>>((acc, [day, timeArray]) => {
                acc[day] = timeArray.map((item, index) => ({
                    value: String(index),
                    label: getTimeslotTimes(item, t)
                }));
                return acc;
            }, {}),
        [options, t]
    );
    const initialValues = React.useMemo(() => {
        const currentDay = timeslot ? getDay(timeslot.start) : Object.keys(options)[0];
        const newInitials: TimeslotFormData = {
            day: currentDay,
            time: timeslot
                ? {
                      [currentDay]: String(
                          options[currentDay]?.findIndex(predicate => predicate.start === timeslot.start)
                      )
                  }
                : Object.keys(options).reduce<Record<string, string>>((acc, day) => {
                      acc[day] = timeOptions[day][0].value;
                      return acc;
                  }, {})
        };
        return newInitials;
    }, [options, timeOptions, timeslot]);
    const handleChange = React.useCallback((_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        setOrderAheadType(value);
    }, []);
    const handleSubmit = React.useCallback(
        (data: TimeslotFormData) => {
            onSubmit(
                orderAheadType === OrderAheadTypes.ASAP
                    ? undefined
                    : { day: data.day, time: data.time[data.day] }
            );
        },
        [onSubmit, orderAheadType]
    );
    const renderTimeSelect = React.useCallback(
        (day: string) => (item: string) => {
            if (day === item) {
                return (
                    <Grid key={`timeslot-picker-${item}`} size={6}>
                        <MultiSelectFormField
                            selectClasses={{ select: classes.boldText }}
                            name={`time[${item}]`}
                            options={timeOptions[item] || []}
                            MenuProps={{ classes: { paper: classes.selectBox, list: classes.selectBoxList } }}
                            MenuItemClasses={{ selected: classes.boldText }}
                        />
                    </Grid>
                );
            }
            return null;
        },
        [timeOptions]
    );

    return (
        <MerchantBrandedDialog
            title={t('MENU_COLLECTION')}
            subtitle={description ? description : t('ORDER_AHEAD_SELECTION_TITLE')}
            icon={<StyledPickUpIcon />}
            open={open}
            onClose={onClose}
            hideImage={!branded}
            hideTitle={!branded}
        >
            <Box paddingY={1}>
                <FormControl component="fieldset" fullWidth>
                    <RadioGroup value={orderAheadType} onChange={handleChange}>
                        {!noAsap && (
                            <FormControlLabel
                                value="ASAP"
                                control={<Radio color="primary" />}
                                label={t('GENERAL_ASAP')}
                            />
                        )}
                        <FormControlLabel
                            disabled={noTimeslots}
                            value="Later"
                            control={<Radio color="primary" />}
                            label={t('BUTTON_LATER')}
                        />
                    </RadioGroup>
                </FormControl>
            </Box>
            <StyledBox paddingY={1}>
                <Formik onSubmit={handleSubmit} initialValues={initialValues}>
                    {({ submitForm, isSubmitting, isValid, values: { day } }) => (
                        <Form>
                            <Grid container spacing={1}>
                                <Grid size={12}>
                                    <Collapse in={OrderAheadTypes.Later === orderAheadType}>
                                        <Box paddingBottom={1} paddingTop={1}>
                                            <Grid container spacing={2}>
                                                <Grid size={6}>
                                                    <MultiSelectFormField
                                                        selectClasses={{ select: classes.boldText }}
                                                        name="day"
                                                        options={dayOptions}
                                                    />
                                                </Grid>
                                                {Object.keys(timeOptions).map(renderTimeSelect(day))}
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                </Grid>
                                <Grid size={12}>
                                    <LoadingButton
                                        loading={isSubmitting}
                                        disabled={isSubmitting || !isValid}
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        onClick={submitForm}
                                    >
                                        {confirmButton
                                            ? confirmButton
                                            : t('DIALOG_TIME_SLOT_PICKER_PICK_BTN')}
                                    </LoadingButton>
                                </Grid>
                                {cancelButton && (
                                    <Grid size={12}>
                                        <Box width="100%">
                                            <Button variant="contained" fullWidth onClick={onCancelOrder}>
                                                {cancelButton}
                                            </Button>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </StyledBox>
        </MerchantBrandedDialog>
    );
};
